import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  Column,
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  RowValue,
} from "../../../../../../components";
import {
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  PAGE_IDS,
} from "../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getValueOrBlank,
  getValueOrDash,
  isEmpty,
  isValueIncludedIgnoreCase,
} from "../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { isProjectFeeType } from "../../../../helper";
import {
  ProjectInvoice,
  ViewProjectInvoiceFeesAndExpenses,
  ViewProjectInvoiceFeesAndExpensesListItem,
} from "../../../../modelsAndSchemas/Models";
import { projectInvoiceHistorySiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";

const pageId = `${PAGE_IDS.PROGRAM_TEMPLATE}-invoice-history-list`;

interface ProjectInvoiceFeesAndExpensesState {
  filteredProjectInvoiceFeesAndExpensesList: ViewProjectInvoiceFeesAndExpensesListItem[];
  originalProjectInvoiceFeesAndExpensesList: ViewProjectInvoiceFeesAndExpensesListItem[];
}

export const ViewProjectInvoiceFeesAndExpensesList = ({
  data,
  getFormattedValue,
}: {
  data: ViewProjectInvoiceFeesAndExpensesListItem[];
  getFormattedValue: (value: number | undefined) => string;
}) => {
  const {
    commonLabels,
    fetchLabel,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const projectInvoiceTableRef = useRef<{ resetPagination: () => void }>(null);
  const { control } = useForm<Partial<ProjectInvoice>>();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const filtersWatch = useWatch({ control });

  const getProjectInvoiceFeesAndExpensesListLabel = (
    key: keyof typeof projectInvoiceHistorySiteCoreKeys,
  ) => {
    return fetchLabel(
      projectInvoiceHistorySiteCoreKeys.invoiceHistoryTitle,
      projectInvoiceHistorySiteCoreKeys[key],
    );
  };

  const getProjectFeeLabel = () => {
    return getProjectInvoiceFeesAndExpensesListLabel("projectFee");
  };

  const projectFeeLabel = getProjectFeeLabel();

  const projectExpenseName = (
    item: ViewProjectInvoiceFeesAndExpensesListItem,
  ) => (isProjectFeeType(item.typeId) ? projectFeeLabel : item?.name || "");

  useEffect(() => {
    applyFilters(
      projectInvoiceFeesAndExpensesListState.originalProjectInvoiceFeesAndExpensesList,
    );
  }, [filtersWatch]);

  const applyFilters = (
    projectInvoiceFeesAndExpenseData: ViewProjectInvoiceFeesAndExpensesListItem[] = [],
  ) => {
    if (!projectInvoiceFeesAndExpenseData.length) return;

    let filteredProjectInvoiceFeesAndExpensesList =
      projectInvoiceFeesAndExpenseData.map((item) => ({
        ...item,
        name: projectExpenseName(item),
      }));

    const {
      name,
      transactionDate,
      typeId,
      expenseCategoryName,
      expenseTypeName,
      billingAmount,
      taxAmount,
      totalBillingAmount,
      lineNumber,
    } = filtersWatch || {};

    filteredProjectInvoiceFeesAndExpensesList =
      filteredProjectInvoiceFeesAndExpensesList.filter((item) => {
        const projectExpenseNameValue = projectExpenseName(item);
        return (
          isValueIncludedIgnoreCase(item.lineNumber, lineNumber) &&
          isValueIncludedIgnoreCase(projectExpenseNameValue, name) &&
          isValueIncludedIgnoreCase(
            convertToUserFormat(item.transactionDate, userCountryDateFormat),
            transactionDate,
          ) &&
          isValueIncludedIgnoreCase(item.typeId, typeId) &&
          isValueIncludedIgnoreCase(
            getValueOrBlank(item.expenseCategoryName),
            expenseCategoryName,
          ) &&
          isValueIncludedIgnoreCase(
            getValueOrBlank(item.expenseTypeName),
            expenseTypeName,
          ) &&
          isValueIncludedIgnoreCase(item.billingAmount, billingAmount) &&
          isValueIncludedIgnoreCase(item.taxAmount, taxAmount) &&
          isValueIncludedIgnoreCase(item.totalBillingAmount, totalBillingAmount)
        );
      });

    setProjectInvoiceFeesAndExpensesListState(
      (prevState: ProjectInvoiceFeesAndExpensesState) => ({
        ...prevState,
        filteredProjectInvoiceFeesAndExpensesList:
          filteredProjectInvoiceFeesAndExpensesList,
      }),
    );
    projectInvoiceTableRef.current?.resetPagination();
  };

  const [
    projectInvoiceFeesAndExpensesListState,
    setProjectInvoiceFeesAndExpensesListState,
  ] = useState<ProjectInvoiceFeesAndExpensesState>({
    filteredProjectInvoiceFeesAndExpensesList: data,
    originalProjectInvoiceFeesAndExpensesList: data,
  });

  useEffect(() => {
    if (data) {
      const formattedData = data.map((item) => ({
        ...item,
        expenseCategoryName: item?.expenseCategories?.name,
        expenseTypeName: item?.expenseTypes?.name,
        name: projectExpenseName(item),
      }));
      setProjectInvoiceFeesAndExpensesListState({
        filteredProjectInvoiceFeesAndExpensesList: formattedData,
        originalProjectInvoiceFeesAndExpensesList: formattedData,
      });
    }
  }, [data, projectFeeLabel]);

  const columns: Column[] = [
    {
      id: ViewProjectInvoiceFeesAndExpenses.projectLineNumber,
      label: getProjectInvoiceFeesAndExpensesListLabel(
        "invoiceLineNumberTableColumn",
      ),
      isSortVisible: true,
    },
    {
      id: ViewProjectInvoiceFeesAndExpenses.projectName,
      label: getProjectInvoiceFeesAndExpensesListLabel(
        "feeOrExpenseNameTableColumn",
      ),
      isSortVisible: true,
    },
    {
      id: ViewProjectInvoiceFeesAndExpenses.projectTransactionDate,
      label: getProjectInvoiceFeesAndExpensesListLabel(
        "transactionDateTableColumn",
      ),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiTypography variant="h3" className="Link">
          {row?.transactionDate
            ? convertToUserFormat(row.transactionDate, userCountryDateFormat)
            : EMPTY_VALUE_DASH}
        </MuiTypography>
      ),
    },
    {
      id: ViewProjectInvoiceFeesAndExpenses.projectTypeId,
      label: getProjectInvoiceFeesAndExpensesListLabel("typeTableColumnView"),
      isSortVisible: true,
      minWidth: 200,
      render: (row: RowProps) => (
        <MuiBox>
          <MuiTypography variant="h3" className="Link">
            {fetchValueByKeyOfDropdown(
              projectInvoiceHistorySiteCoreKeys.invoiceHistoryTitle,
              projectInvoiceHistorySiteCoreKeys.billingItemTypeList,
              row.typeId,
            )}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ViewProjectInvoiceFeesAndExpenses.projectExpenseCategoryName,
      label: getProjectInvoiceFeesAndExpensesListLabel(
        "categoryTableColumnView",
      ),
      isSortVisible: true,
      minWidth: 200,
      render: (row: RowProps) => (
        <MuiBox className="cell">
          <MuiTypography variant="h3">
            {getValueOrDash(row?.expenseCategoryName)}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ViewProjectInvoiceFeesAndExpenses.projectExpenseTypeName,
      label: getProjectInvoiceFeesAndExpensesListLabel(
        "feeOrExpenseTypeTableColumnView",
      ),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiBox className="cell">
          <MuiTypography variant="h3">
            {getValueOrDash(row?.expenseTypeName)}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ViewProjectInvoiceFeesAndExpenses.projectBillingAmount,
      label: getProjectInvoiceFeesAndExpensesListLabel(
        "billingAmountTableColumnView",
      ),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiBox className="cell">
          <MuiTypography variant="h3">
            {getFormattedValue(row?.billingAmount)}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ViewProjectInvoiceFeesAndExpenses.projectTaxAmount,
      label: getProjectInvoiceFeesAndExpensesListLabel(
        "taxAmountTableColumnView",
      ),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiBox className="cell">
          <MuiTypography variant="h3">
            {getFormattedValue(row?.taxAmount)}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ViewProjectInvoiceFeesAndExpenses.projectTotalBillingAmount,
      label: getProjectInvoiceFeesAndExpensesListLabel(
        "FeeAndExpensesTotalInvoiceAmountTableColumn",
      ),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiBox className="cell">
          <MuiTypography variant="h3">
            {getFormattedValue(row?.totalBillingAmount)}
          </MuiTypography>
        </MuiBox>
      ),
    },
  ];

  const renderSearchIcon = () => (
    <InputAdornment position="start">
      <SearchIcon />
    </InputAdornment>
  );

  const tableHeaderForm: React.JSX.Element[] = [
    <FormController
      control={control}
      name={ViewProjectInvoiceFeesAndExpenses.projectLineNumber}
      key={ViewProjectInvoiceFeesAndExpenses.projectLineNumber}
    >
      <MuiTextField
        id={`${pageId}-${ViewProjectInvoiceFeesAndExpenses.projectLineNumber}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ViewProjectInvoiceFeesAndExpenses.projectName}
      key={ViewProjectInvoiceFeesAndExpenses.projectName}
    >
      <MuiTextField
        id={`${pageId}-${ViewProjectInvoiceFeesAndExpenses.projectName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <MuiDateFieldFormController
      control={control}
      clearable
      name={ViewProjectInvoiceFeesAndExpenses.projectTransactionDate}
      key={ViewProjectInvoiceFeesAndExpenses.projectTransactionDate}
      format={userCountryDateFormat}
      valueFormat={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
    />,

    <FormController
      control={control}
      name={ViewProjectInvoiceFeesAndExpenses.projectTypeId}
      key={ViewProjectInvoiceFeesAndExpenses.projectTypeId}
    >
      <MuiSelect
        label=""
        options={fetchDropdownValues(
          projectInvoiceHistorySiteCoreKeys.invoiceHistoryTitle,
          projectInvoiceHistorySiteCoreKeys.billingItemTypeList,
        )}
        visibleDefaultSelectOption={true}
        value=""
        id={`${pageId}-${ViewProjectInvoiceFeesAndExpenses.projectTypeId}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ViewProjectInvoiceFeesAndExpenses.projectExpenseCategoryName}
      key={ViewProjectInvoiceFeesAndExpenses.projectExpenseCategoryName}
    >
      <MuiTextField
        id={`${pageId}-${ViewProjectInvoiceFeesAndExpenses.projectExpenseCategoryName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ViewProjectInvoiceFeesAndExpenses.projectExpenseTypeName}
      key={ViewProjectInvoiceFeesAndExpenses.projectExpenseTypeName}
    >
      <MuiTextField
        id={`${pageId}-${ViewProjectInvoiceFeesAndExpenses.projectExpenseTypeName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ViewProjectInvoiceFeesAndExpenses.projectBillingAmount}
      key={ViewProjectInvoiceFeesAndExpenses.projectBillingAmount}
    >
      <MuiTextField
        id={`${pageId}-${ViewProjectInvoiceFeesAndExpenses.projectBillingAmount}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ViewProjectInvoiceFeesAndExpenses.projectTaxAmount}
      key={ViewProjectInvoiceFeesAndExpenses.projectTaxAmount}
    >
      <MuiTextField
        id={`${pageId}-${ViewProjectInvoiceFeesAndExpenses.projectTaxAmount}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ViewProjectInvoiceFeesAndExpenses.projectTotalBillingAmount}
      key={ViewProjectInvoiceFeesAndExpenses.projectTotalBillingAmount}
    >
      <MuiTextField
        id={`${pageId}-${ViewProjectInvoiceFeesAndExpenses.projectTotalBillingAmount}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
  ];

  return (
    <MuiTable
      ref={projectInvoiceTableRef}
      pageId={pageId}
      tableHeaderForm={tableHeaderForm}
      columns={columns}
      tableResults={
        projectInvoiceFeesAndExpensesListState.filteredProjectInvoiceFeesAndExpensesList as unknown as Record<
          string,
          RowValue
        >[]
      }
      noDataInApi={isEmpty(
        projectInvoiceFeesAndExpensesListState.originalProjectInvoiceFeesAndExpensesList,
      )}
      noResultMessage={commonLabels.searchCriteria}
      priorityResults={[]}
      isShowDashIfValueEmpty
    />
  );
};
